import React, { ReactElement, useState } from 'react'
import { Alert, Box, Typography } from '@mui/material'

import { TrashIcon } from '~assets/icons'
import { Button } from '~components/common'
import { lightTheme } from '~createTheme'

interface RemoveEntryButtonProps<T> {
  row: T
  onRemove: (row: T) => void
  itemName?: string
  isDisabled?: boolean
  showConfirmation?: boolean
  showWarning?: boolean
  warningMessage?: string
}

function RemoveEntryButton<T>({
  row,
  onRemove,
  itemName,
  isDisabled = false,
  showConfirmation = true,
  showWarning = true,
  warningMessage = 'Are you sure you want to delete this item? This action cannot be undone.',
}: RemoveEntryButtonProps<T>): ReactElement {
  const [confirmationActive, setConfirmationActive] = useState(false)

  const handleDeleteClick = (): void => {
    if (showConfirmation) {
      setConfirmationActive(true)
    } else {
      handleConfirmDelete()
    }
  }

  const handleCancelDelete = (): void => {
    if (showConfirmation) {
      setConfirmationActive(false)
    }
  }

  const handleConfirmDelete = (): void => {
    onRemove(row)
    if (showConfirmation) {
      setConfirmationActive(false)
    }
  }

  return (
    <Box display='flex' flexDirection='column' alignItems='flex-start'>
      <Box display='flex' alignItems='center'>
        {!confirmationActive ? (
          <Button size='large' onClick={handleDeleteClick} disabled={isDisabled}>
            <TrashIcon
              color={isDisabled ? lightTheme.palette.grey[400] : lightTheme.palette.text.primary}
            />
            {itemName && (
              <Typography
                variant='body2'
                color={isDisabled ? lightTheme.palette.grey[400] : lightTheme.palette.text.primary}
              >
                {' '}
                {`Delete ${itemName}`}{' '}
              </Typography>
            )}
          </Button>
        ) : (
          <>
            <Button size='large' variant='text' onClick={handleConfirmDelete} sx={{ margin: 1 }}>
              <TrashIcon color={lightTheme.palette.grey[600]} />
              <Typography variant='body2' color='secondary'>
                Confirm Delete
              </Typography>
            </Button>
            <Button
              size='large'
              variant='text'
              color='secondary'
              onClick={handleCancelDelete}
              sx={{ margin: 1 }}
            >
              <Typography variant='body2'>Cancel</Typography>
            </Button>
          </>
        )}
      </Box>
      {showConfirmation && confirmationActive && showWarning && (
        <Box mt={2} width='100%'>
          <Alert severity='warning' sx={{ mb: 2 }}>
            {warningMessage}
          </Alert>
        </Box>
      )}
    </Box>
  )
}

export default RemoveEntryButton
